import { keyBy } from "lodash";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { DataById, HotspotMaker } from "../../utils/types";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";

interface ContextValue {
  hotspotMakersById: DataById<HotspotMaker>;
  getHotspotMakerName: (hotspotPayer: string) => string;
  featuredHotspotVendors: HotspotVendor[];
}

const HotspotMakersContext = React.createContext<ContextValue | undefined>(
  undefined
);

const HotspotMakersProvider = ({ children }: PropsWithChildren<{}>) => {
  const { addLoader, removeLoader } = useLoading();
  const [hotspotMakersById, setHotspotMakersById] = useState<
    DataById<HotspotMaker>
  >({});

  const fetchHotspotMakers = useCallback(async () => {
    addLoader(LoadingKey.hotspotMakers);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ETL_API}/hotspots/makers`
      );

      const apiResponse: HotspotMaker[] = await response.json();

      setHotspotMakersById(keyBy(apiResponse, "maker_id"));
    } catch (error) {
      console.error(error);
    }

    removeLoader(LoadingKey.hotspotMakers);
  }, [addLoader, removeLoader]);

  useEffect(() => {
    fetchHotspotMakers();
  }, [fetchHotspotMakers]);

  const getHotspotMakerName = useCallback(
    (hotspotPayer: string) => {
      const hotspotMaker = hotspotMakersById[hotspotPayer];
      const metadata =
        hotspotMakerMetadata[hotspotPayer as HotspotMakerAddressKey];
      return metadata?.name || hotspotMaker?.maker_name || "Unknown";
    },
    [hotspotMakersById]
  );

  const featuredHotspotVendors: HotspotVendor[] = useMemo(() => {
    const allVendors = [
      {
        key: "parley-labs",
        name: "Parley Labs",
        purchaseLink: "https://bit.ly/parley-labs",
        hidden: false
      }
    ];

    return allVendors.filter((vendor) => !vendor.hidden);
  }, []);

  const value = useMemo(
    () => ({
      hotspotMakersById,
      getHotspotMakerName,
      featuredHotspotVendors
    }),
    [hotspotMakersById, getHotspotMakerName, featuredHotspotVendors]
  );

  return (
    <HotspotMakersContext.Provider value={value}>
      {children}
    </HotspotMakersContext.Provider>
  );
};

const useHotspotMakers = (): ContextValue => {
  const context = useContext(HotspotMakersContext);
  if (context === undefined) {
    throw new Error(
      "useHotspotMakers must be used within an HotspotMakersProvider"
    );
  }
  return context;
};

export { useHotspotMakers, HotspotMakersProvider };

//
// Utils
//

export enum HotspotMakerAddressKey {
  browanMerryIoT = "14iLrXUuGVhb7w1P8X3iBvRwoT9oemgbDonm5VVZJk56TdV8NV1",
  cotxNetworks = "13cbbZXzqwp6YMM5JvAu5T1TRhenENEJVU5Q8vpLhunQYE1Acpp",
  rakWireless = "14h2zf1gEr9NmvDb2U53qucLN2jLrKU1ECBoxGnSnQ6tiT6V2kM",
  smartMimic = "13MS2kZHU4h6wp3tExgoHdDFjBsb9HB9JBvcbK9XmfNyJ7jqzVv",
  nebraLtd = "13Zni1he7KY9pUmkXMhEhTwfUpL9AcEV1m2UbbvFsrU9QPTMgE3",
  senseCAP = "14NBXJE5kAAZTMigY4dcjXSMG4CSqjYwvteQWwQsYhsu2TKN6AF",
  piscesMiner = "134C7Hn3vhfBLQZex4PVwtxQ2uPJH97h9YD2bhzy1W2XhMJyY6d",
  linxdot = "14eUfY1GsjK4WH6uZYoeagnFtigBKdvPruAXLmc5UsUMEDj3yib",
  syncroBit = "14rb2UcfS9U89QmKswpZpjRCUVCVu1haSyqyGY486EvsYtvdJmR",
  freedomFi = "13y2EqUUzyQhQGtDSoXktz8m5jHNSiwAKLTYnHNxZq2uH5GGGym",
  kerlink = "13Mpg5hCNjSxHJvWjaanwJPBuTXu1d4g5pGvGBkqQe3F8mAwXhK",
  clodPi = "13XuP2DjHEHVkKguDDZD2ev5AeqMLuJ8UQ44efEcDmVTnBcvc6F",
  heltecAutomation = "14iC6N1HkqUjH7WEChHVQhPqJ1hbWBKpZXZVeHHykCA7tNDYF2C",
  heliumInc = "13daGGWvDQyTyHFDCPz8zDSVTWgPNNfJ4oh31Teec4TRWfjMx53",
  hummingbird = "14DdSjvEkBQ46xQ24LAtHwQkAeoUUZHfGCosgJe33nRQ6rZwPG3",
  milesight = "13H6RpJojJjkqPRfzdrFBDbpYw4b3A6HyMxgGFRgXf31Nuwq4xJ",
  pantherX = "13v9iGhjvQUtVaZXcFFRCEbL1nPR4R8QJowBgMUcaGM2v1aV6mn",
  controllino = "14go8hvEDnotWTyhYv6Hu5PTnRUAQzJqbB6dsDm1oThkCcZe9zd",
  calChipConnectedDevices = "13ENbEQPAvytjLnqavnbSAzurhGoCSNkGECMx7eHHDAfEaDirdY",
  bobcat = "14sKWeeYWQWrBSnLGq79uRQqZyw3Ldi7oBdxbF6a54QboTNBXDL",
  risingHF = "143QF5dTGyAg1FqaE85mPApvjauFU8fUy6wavirQfJJDgSkC4gn",
  heliumOG = "14fzfjFcHpDR1rTH8BNPvSi5dKBbgxaDnmsVPbCjuq9ENjpZbxh",
  longAP = "12zX4jgDGMbJgRwmCfRNGXBuphkQRqkUTcLzYHTQvd4Qgu8kiL4",
  deWiFoundation = "13LVwCqZEKLTVnf3sjGPY1NMkTE7fWtUVjmDfeuscMFgeK3f9pn",
  deeper = "146S5XCtvB1VwqYzJnRr8goiADUFTscGAUb2ihKUBVYuvMrrNCx",
  makerIntegrationTests = "138LbePH4r7hWPuTnK6HXVJ8ATM2QU71iVHzLTup1UbnPDvbxmr",
  edaIot = "136SdnfVaGvQn5uTrTMtcKBXeQueqP8vJUdQQR1nier1fhNK9Ny",
  dusun = "14HQjuyvr8jj4ECp2jq9Y5riXZBhzekZRD7g28pvrYngXRzX368",
  embit = "14cJRHZan9fmUH4XpA8XD5YkqsEbWm2pqm524ewFMoRa8gzw3XB",
  bobcat5g = "14gqqPV2HEs4PCNNUacKVG7XeAhCUkN553NcBVw4xfwSFcCjhXv",
  pycom = "14ZVsWQ8D98NCW673qxNepcYu1nfBiuuF6rhU2JHCFpfgqtnc5S",
  midas = "13eZdVcXajB3HrxKqeoPRPVPXbpqbDbaFpey5KfALk44VUphC5Z",
  atom = "13GY3FdbfNpXoBkyRukFj2fx9851oqX2SUTKY8Gd4BDJsZsuYhd",
  aitek = "14KKDX2N56h1xQEQVg6Um4myYUoG7Xes3P5scfwn9B5y5cY8e4t",
  dragino = "13gb8SJg8MZSyLakrx8ago146Gxk7tpvY4R3kgMSYGzAKE188m2",
  osprey = "148i6YAg4N7rSqABdTygCFYh7g2QGJ7KeRbskWc7cZgvgQNsbq2",
  KSTechnologies = "14BHX3FQUNnRZSXohHpwyYEEhymYaPxyDir4bJUPsCYa3czBJP4",
  uGMiner = "14pmUdhaq7AYznDV8ST37yztmX1FmPQJabxsDjsAuUrDatxM4qK"
}

export const unlistedMakers = [
  HotspotMakerAddressKey.deWiFoundation,
  HotspotMakerAddressKey.makerIntegrationTests
];

type Labels =
  | "IOT"
  | "MOBILE"
  | "BANNED"
  | "Remote Diagnostics"
  | "Hotspotty Partner";

export const hotspotMakerMetadata: {
  [payer in HotspotMakerAddressKey]: {
    name?: string;
    logoUrl?: string;
    labels: Labels[];
    purchaseLink?: string;
    communityLink?: string;
    discordLink?: string;
    twitterLink?: string;
    instagramLink?: string;
    linkedinLink?: string;
    facebookLink?: string;
    redditLink?: string;
    telegramLink?: string;
    youtubeLink?: string;
    supportLink?: string;
    emailSupportLink?: string;
    emailSalesLink?: string;
    blogLink?: string;
    phoneLink?: string;
    githubLink?: string;
    remoteDiagnostics?: boolean;
  };
} = {
  [HotspotMakerAddressKey.browanMerryIoT]: { labels: ["IOT"] },
  [HotspotMakerAddressKey.cotxNetworks]: {
    logoUrl: "/makers/cotx.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3CF0bXU",
    facebookLink: "https://www.facebook.com/CotxNetworks",
    linkedinLink:
      "https://www.linkedin.com/company/beijing-cotx-networks-technologies-co-ltd/mycompany/",
    twitterLink: "https://twitter.com/CotxNetworks",
    discordLink: "https://discord.gg/s8NAKj7Vfx",
    supportLink: "https://www.cotxnetworks.com/about/contact/",
    phoneLink: "tel:+86-0755-36607728",
    emailSupportLink: "mailto:techsupport@cotxnetworks.com",
    emailSalesLink: "mailto:sales@cotxnetworks.com"
  },
  [HotspotMakerAddressKey.rakWireless]: {
    name: "RAKwireless / MNTD.",
    logoUrl: "/makers/mntd.jpeg",
    labels: ["IOT", "MOBILE", "Remote Diagnostics", "Hotspotty Partner"],
    purchaseLink: "https://bit.ly/mntd-hotspotty",
    emailSupportLink: "mailto:heliumsupport@rakwireless.com",
    discordLink: "https://discord.com/invite/gae6cGa7jn",
    twitterLink: "https://twitter.com/getmntd_",
    instagramLink: "https://www.instagram.com/getmntd_/",
    linkedinLink: "https://www.linkedin.com/company/getmntd/",
    facebookLink: "https://www.facebook.com/GetMNTD/",
    redditLink: "https://www.reddit.com/r/getmntd/",
    telegramLink: "https://t.me/getmntd",
    youtubeLink: "https://www.youtube.com/MNTDbyRAKwireless",
    remoteDiagnostics: true
  },
  [HotspotMakerAddressKey.smartMimic]: {
    name: "Finestra Miner",
    logoUrl: "/makers/finestra.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3x8nRCN",
    supportLink: "https://thefinestra.com/support/",
    blogLink: "https://thefinestra.com/blog/"
  },
  [HotspotMakerAddressKey.nebraLtd]: {
    name: "Nebra",
    logoUrl: "/makers/nebra.png",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3czCK7C",
    supportLink: "https://www.nebra.com/pages/contact-us",
    emailSupportLink: "mailto:support@nebra.com",
    emailSalesLink: "mailto:sales@nebra.com",
    facebookLink: "https://www.facebook.com/NebraLtd?ref=hotspotty",
    twitterLink: "https://twitter.com/nebraltd",
    instagramLink: "https://instagram.com/nebraltd?ref=hotspotty",
    youtubeLink:
      "https://www.youtube.com/channel/UCAB2dClW-LV8jgDgCrsmFwQ?ref=hotspotty"
  },
  [HotspotMakerAddressKey.senseCAP]: {
    logoUrl: "/makers/sensecapmx.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3kTTuey",
    supportLink: "https://www.sensecapmx.com/contact-us/",
    emailSupportLink: "mailto:support@sensecapmx.com",
    discordLink: "https://discord.gg/zxTMu65v8C",
    twitterLink: "https://twitter.com/sensecapmx",
    blogLink: "https://www.sensecapmx.com/blogs/"
  },
  [HotspotMakerAddressKey.piscesMiner]: {
    logoUrl: "/makers/pisces.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3CD8mns",
    emailSalesLink: "mailto:sale@piscesminer.com",
    emailSupportLink: "mailto:support@piscesminer.com",
    discordLink: "https://discord.gg/j7rYR4GT7u",
    twitterLink: "https://twitter.com/Piscesminer"
  },
  [HotspotMakerAddressKey.linxdot]: {
    logoUrl: "/makers/linxdot.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3CA738P",
    supportLink: "https://www.linxdot.com/support/",
    emailSalesLink: "mailto:hello@linxdot.com",
    twitterLink: "https://twitter.com/linxdot",
    facebookLink: "https://www.facebook.com/Linxdot",
    discordLink: "https://www.linxdot.com/discord"
  },
  [HotspotMakerAddressKey.syncroBit]: {
    logoUrl: "/makers/syncrobit.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3CGlmZr",
    supportLink: "https://syncrob.it/contact/?ref=hotspotty",
    emailSupportLink: "mailto:support@syncrob.it",
    blogLink: "https://syncrobit.medium.com/",
    discordLink: "https://discord.com/invite/CyjdUtUJ"
  },
  [HotspotMakerAddressKey.freedomFi]: {
    logoUrl: "/makers/freedomfi.jpeg",
    labels: ["MOBILE", "IOT"],
    purchaseLink: "https://bit.ly/freedomfi-hotspotty",
    emailSupportLink: "mailto:support@freedomfi.com",
    supportLink: "https://freedomfi.com/contact-us/",
    communityLink:
      "https://freedomfi.atlassian.net/servicedesk/customer/portal/1/user/login?destination=portal%2F1",
    youtubeLink: "https://www.youtube.com/c/FreedomFi",
    twitterLink: "https://twitter.com/open5g",
    githubLink: "https://github.com/magma/magma",
    blogLink: "https://freedomfi.com/blog/"
  },
  [HotspotMakerAddressKey.kerlink]: {
    logoUrl: "/makers/kerlink.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/32cHZrX",
    supportLink: "https://www.kerlink.com/customer-support/",
    linkedinLink: "https://www.linkedin.com/company/kerlink",
    twitterLink: "https://twitter.com/kerlink_news",
    youtubeLink: "https://www.youtube.com/channel/UCXR60-5v1ksOD3r4NW-lLyw",
    discordLink: "https://discord.com/invite/jM7HRW9V",
    blogLink: "https://www.kerlink.com/blog/category/blog/"
  },
  [HotspotMakerAddressKey.clodPi]: {
    logoUrl: "/makers/clodpi.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/30I8uVn",
    supportLink: "https://india-shop.clodpi.io/contact",
    discordLink: "https://discord.com/invite/unJjAtZHBZ",
    twitterLink: "https://twitter.com/clodpilabs",
    facebookLink: "https://www.facebook.com/clodpilabs/",
    linkedinLink: "https://www.linkedin.com/company/clodpi-labs/",
    phoneLink: "tel:+919036360110",
    emailSalesLink: "mailto:sales@clodpi.io",
    emailSupportLink: "mailto:support@clodpi.io"
  },
  [HotspotMakerAddressKey.heltecAutomation]: {
    logoUrl: "/makers/heltec-automation.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3DzWnbv",
    emailSupportLink: "mailto:support@heltec.cn",
    phoneLink: "tel:+86-028-62374838",
    discordLink: "https://discord.gg/Pt85TrZfxZ",
    facebookLink: "https://www.facebook.com/heltec.org",
    twitterLink: "https://twitter.com/HeltecOrg",
    githubLink: "https://github.com/HelTecAutomation"
  },
  [HotspotMakerAddressKey.heliumInc]: {
    logoUrl: "/makers/helium.jpeg",
    labels: ["IOT"],
    blogLink: "https://blog.helium.com/",
    twitterLink: "https://twitter.com/helium",
    facebookLink: "https://www.facebook.com/heliumsystems",
    linkedinLink: "https://www.linkedin.com/company/heliuminc",
    githubLink: "https://github.com/helium",
    telegramLink: "https://t.me/helium_network",
    youtubeLink: "https://www.youtube.com/c/HeliumInc",
    redditLink: "https://www.reddit.com/r/HeliumNetwork",
    instagramLink: "https://www.instagram.com/helium/",
    discordLink: "https://discord.gg/helium",
    emailSalesLink: "mailto:sales@helium.com",
    supportLink: "https://docs.helium.com/faq/helium-network/"
  },
  [HotspotMakerAddressKey.heliumOG]: {
    logoUrl: "/makers/helium.jpeg",
    labels: ["IOT"],
    blogLink: "https://blog.helium.com/",
    twitterLink: "https://twitter.com/helium",
    facebookLink: "https://www.facebook.com/heliumsystems",
    linkedinLink: "https://www.linkedin.com/company/heliuminc",
    githubLink: "https://github.com/helium",
    telegramLink: "https://t.me/helium_network",
    youtubeLink: "https://www.youtube.com/c/HeliumInc",
    redditLink: "https://www.reddit.com/r/HeliumNetwork",
    instagramLink: "https://www.instagram.com/helium/",
    discordLink: "https://discord.gg/helium",
    emailSalesLink: "mailto:sales@helium.com",
    supportLink: "https://docs.helium.com/faq/helium-network/"
  },
  [HotspotMakerAddressKey.hummingbird]: {
    logoUrl: "/makers/hummingbird.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3CBVBcV",
    supportLink: "https://www.xdt.com/faq.html",
    twitterLink: "https://twitter.com/hummingbird_66",
    telegramLink: "https://t.me/HummingbirdCommunity",
    discordLink: "https://discord.gg/XVR7fRKaDd",
    emailSupportLink: "mailto:product@xdt.com"
  },
  [HotspotMakerAddressKey.milesight]: {
    logoUrl: "/makers/milesight.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3HG0wNR",
    supportLink: "https://support.milesight-iot.com/hc/en-us/requests/new",
    linkedinLink: "https://www.linkedin.com/company/milesightiot",
    youtubeLink: "https://www.youtube.com/c/MilesightIoT",
    twitterLink: "https://twitter.com/MilesightIoT",
    facebookLink: "https://www.facebook.com/MilesightIoT",
    instagramLink: "https://www.instagram.com/milesightiot/",
    githubLink: "https://github.com/Milesight-IoT",
    emailSalesLink: "mailto:iot.sales@milesight.com",
    emailSupportLink: "mailto:iot.support@milesight.com",
    phoneLink: "tel:86-592-5085280"
  },
  [HotspotMakerAddressKey.pantherX]: {
    logoUrl: "/makers/pantherx.jpeg",
    labels: ["IOT", "BANNED"],
    purchaseLink: "https://bit.ly/3xswNmR",
    twitterLink: "https://twitter.com/PantherMiner",
    instagramLink: "https://www.instagram.com/panther.miner/",
    discordLink: "https://discord.com/invite/CQCpPNeekn",
    telegramLink: "https://t.me/OfficialPantherGlobal",
    emailSupportLink: "mailto:service@panther.global"
  },
  [HotspotMakerAddressKey.controllino]: {
    logoUrl: "/makers/controllino.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/controllino",
    supportLink: "https://hotspot.controllino.com/support/",
    discordLink: "https://discord.gg/z8kxkgMn44",
    twitterLink: "https://twitter.com/CONTROLLINO",
    emailSupportLink: "mailto:hotspotsupport@controllino.com",
    emailSalesLink: "mailto:hotspotsales@controllino.com"
  },
  [HotspotMakerAddressKey.calChipConnectedDevices]: {
    logoUrl: "/makers/calchip.jpeg",
    labels: ["IOT", "MOBILE", "Remote Diagnostics"],
    name: "Cal-Chip",
    purchaseLink: "https://bit.ly/3x6XP2F",
    twitterLink: "https://twitter.com/CalChipConnect",
    supportLink: "https://www.calchipconnect.com/pages/contact-us",
    emailSupportLink: "mailto:support@calchipconnect.com",
    remoteDiagnostics: true
  },
  [HotspotMakerAddressKey.bobcat]: {
    logoUrl: "/makers/bobcat.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/3DA9ytl",
    supportLink: "https://www.bobcatminer.com/contact",
    discordLink: "https://discord.com/invite/2WwMMBfYce",
    twitterLink: "https://twitter.com/BOBCATiot",
    linkedinLink: "https://www.linkedin.com/company/bobcatminer",
    instagramLink: "https://www.instagram.com/bobcat_miner/"
  },
  [HotspotMakerAddressKey.risingHF]: {
    logoUrl: "/makers/risinghf.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/328S4WU",
    discordLink: "https://discord.gg/x4ECnU7VqS",
    twitterLink: "https://twitter.com/RisingHf",
    phoneLink: "tel:0755-86529656",
    emailSalesLink: "mailto:salesww@risinghf.com",
    emailSupportLink: "mailto:support@risinghf.com"
  },
  [HotspotMakerAddressKey.longAP]: {
    logoUrl: "/makers/longap.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/2Z7ZwR6",
    emailSupportLink: "mailto:support@longap.com",
    emailSalesLink: "mailto:sales@longap.com",
    twitterLink: "https://twitter.com/longapcom",
    blogLink: "https://longap.com/updates/"
  },
  [HotspotMakerAddressKey.deWiFoundation]: {
    labels: ["IOT"],
    twitterLink: "https://twitter.com/dewialliance",
    blogLink: "https://medium.com/@dewialliance"
  },
  [HotspotMakerAddressKey.deeper]: {
    name: "Deeper Network",
    logoUrl: "/makers/deeper-network.jpeg",
    labels: ["IOT", "BANNED"],
    twitterLink: "https://twitter.com/deeper_network",
    telegramLink: "https://t.me/DeeperNetwork",
    facebookLink: "https://www.facebook.com/deeper.network",
    instagramLink: "https://www.instagram.com/deeper.network/?hl=en",
    youtubeLink: "https://www.youtube.com/channel/UC9lqgUL1MgWXx1wj4FaTE7A",
    redditLink: "https://www.reddit.com/r/DeeperNetwork/",
    blogLink: "https://deeper-network.medium.com/",
    discordLink: "https://discord.gg/EWUyAJyH2B",
    supportLink: "https://support.deeper.network/hc/en-us"
  },
  [HotspotMakerAddressKey.makerIntegrationTests]: { labels: ["IOT"] },
  [HotspotMakerAddressKey.edaIot]: {
    name: "EDA IoT",
    logoUrl: "/makers/eda-iot.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://www.eda-iot.com/",
    twitterLink: "https://twitter.com/EDAIoT",
    emailSalesLink: "mailto:sales@edatec.cn",
    emailSupportLink: "mailto:support@edatec.cn",
    discordLink: "https://discord.gg/SKwEeSreJu",
    phoneLink: "tel:+86-18217351262"
  },
  [HotspotMakerAddressKey.dusun]: {
    logoUrl: "/makers/dusun.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/dusuniot",
    facebookLink: "https://www.facebook.com/dusuniot",
    linkedinLink: "https://www.linkedin.com/in/dusun-iot-40081a190/",
    twitterLink: "https://twitter.com/Dusunelectron",
    discordLink: "https://discord.gg/d5b2Y2rUsv",
    youtubeLink: "https://www.youtube.com/channel/UCyb4PpqVgvKgC9KpkByZaaQ",
    blogLink: "https://www.dusuniot.com/blog",
    supportLink: "https://www.dusuniot.com/contact-us",
    phoneLink: "tel:+86057186796578",
    emailSalesLink: "mailto:sales@dusuniot.com"
  },
  [HotspotMakerAddressKey.embit]: {
    logoUrl: "/makers/embit.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://www.embitminer.com",
    linkedinLink: "https://www.linkedin.com/company/embit",
    twitterLink: "https://twitter.com/embitwireless",
    supportLink: "https://www.embitminer.com/new/ticket"
  },
  [HotspotMakerAddressKey.bobcat5g]: {
    logoUrl: "/makers/bobcat.jpeg",
    labels: ["MOBILE"],
    purchaseLink: "https://bit.ly/3DA9ytl",
    supportLink: "https://www.bobcatminer.com/contact",
    discordLink: "https://discord.com/invite/2WwMMBfYce",
    twitterLink: "https://twitter.com/BOBCATiot",
    linkedinLink: "https://www.linkedin.com/company/bobcatminer",
    instagramLink: "https://www.instagram.com/bobcat_miner/"
  },
  [HotspotMakerAddressKey.pycom]: {
    logoUrl: "/makers/pycom.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/pycom-helium",
    facebookLink: "https://www.facebook.com/PycomIOT/",
    linkedinLink: "https://www.linkedin.com/company/pycom",
    twitterLink: "https://twitter.com/pycomiot",
    emailSupportLink: "mailto:support@pycom.io",
    emailSalesLink: "mailto:sales@pycom.io"
  },
  [HotspotMakerAddressKey.midas]: {
    logoUrl: "/makers/midas.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://www.midaswireless.io/",
    instagramLink: "https://www.instagram.com/midaswireless",
    twitterLink: "https://twitter.com/midaswireless",
    discordLink: "https://discord.gg/yFSTWJrA6R",
    supportLink: "https://www.midaswireless.io/ContactUs",
    githubLink: "https://github.com/MidasWireless"
  },
  [HotspotMakerAddressKey.atom]: {
    labels: ["IOT"]
  },
  [HotspotMakerAddressKey.aitek]: {
    logoUrl: "/makers/aitek.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://shop.aiteks.com/products/aitek-miner-plus",
    linkedinLink: "https://www.linkedin.com/company/aitekinc",
    twitterLink: "https://twitter.com/AitekInc",
    discordLink: "https://discord.com/invite/nrVjTE9vBp",
    supportLink: "https://shop.aiteks.com/pages/contact",
    redditLink: "https://www.reddit.com/r/aitekminer/"
  },
  [HotspotMakerAddressKey.dragino]: {
    logoUrl: "/makers/dragino.png",
    labels: ["IOT"],
    purchaseLink: "https://www.dragino.com/products/lora-lorawan-gateway.html",
    facebookLink: "https://www.facebook.com/DraginoTech",
    twitterLink: "https://twitter.com/draginotech",
    supportLink: "https://www.dragino.com/contact.html",
    phoneLink: "tel:+8675586610829",
    emailSalesLink: "mailto:sales@dragino.com"
  },
  [HotspotMakerAddressKey.osprey]: {
    logoUrl: "/makers/osprey.jpeg",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/osprey-electronics",
    twitterLink: "https://twitter.com/OspreyElectron",
    supportLink: "https://www.ospreyelectronics.io/contact",
    blogLink: "https://www.ospreyelectronics.io/blog"
  },
  [HotspotMakerAddressKey.KSTechnologies]: {
    logoUrl: "/makers/KSTechnologies.png",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/KSTechnologies",
    facebookLink:
      "https://www.facebook.com/pages/KS-Technologies-LLC/107015177588",
    linkedinLink: "https://www.linkedin.com/company/ks-technologies-llc",
    githubLink: "https://github.com/kstechnologies",
    twitterLink: "https://twitter.com/kstechnologies",
    instagramLink: "https://www.instagram.com/kstechnologies/",
    supportLink: "https://kstechnologies.com/#contact",
    phoneLink: "tel:719.694.8193",
    emailSupportLink: "mailto:info@kstechnologies.com",
    emailSalesLink: "mailto:sales@kstechnologies.com"
  },
  [HotspotMakerAddressKey.uGMiner]: {
    name: "uGWAN",
    logoUrl: "/makers/uGWAN.png",
    labels: ["IOT"],
    purchaseLink: "https://bit.ly/uGWAN-miners",
    supportLink: "https://bit.ly/uGWAN-miners",
    emailSupportLink: "mailto:support@ugwan.io"
  }
};

export interface HotspotVendor {
  key: string;
  name: string;
  subtitle?: string;
  purchaseLink: string;
  hidden: boolean;
}
